import { create } from "zustand"
import produce from "immer"
import {
  firestoreCommitSessionAssessments,
  firestoreCreatePreAssessment,
  firestoreDeleteAssessment,
  firestoreGetTestAlias,
  firestoreResolveTestAlias,
  firestoreSetClientOutcomeTrackingOptIn,
  firestoreSetMarkedForRetest,
  firestoreSetSessionAssessmentMode,
  firestoreSetSessionOutcome,
  firestoreUpdatePreAssessment,
  firestoreWritePostAssessment,
  userSessionAssessmentsOnSnapshot,
  userTestAliasesOnSnapshot,
} from "../firebase/firestore"
import { buildAdHocTestKey, fuzzyMatchTestLabel, normalizeToSlug } from "../utils/testSlug"
import { POST_STATUS_NOT_RETESTED, POST_STATUS_RETESTED } from "../constants/constants"

/* Per-session assessment store.
   - assessments: live list of per-session assessment docs (snapshot-driven)
   - aliases: map of slug -> alias doc, for resolved ad-hoc test names
   - pendingAliasQueue: ad-hoc dictation findings awaiting therapist confirmation
   - wrapUpOpen / dialogStep: UI flow state for SessionWrapUpDialog (used later) */

export const useAssessmentStore = create((set, get) => ({
  assessments: [],
  aliases: {},
  wrapUpOpen: false,
  dialogStep: null,
  pendingAliasQueue: [],
  unsubscribeAssessments: null,
  unsubscribeAliases: null,

  setAssessments: (assessments) => set({ assessments: assessments ?? [] }),
  setAliases: (aliases) => set({ aliases: aliases ?? {} }),
  setWrapUpOpen: (wrapUpOpen) => set({ wrapUpOpen }),
  setDialogStep: (dialogStep) => set({ dialogStep }),

  subscribeForSession: ({ uid, sessionId }) => {
    if (!uid || !sessionId) return
    const { unsubscribeAssessments, unsubscribeAliases, setAssessments, setAliases } = get()
    if (unsubscribeAssessments) unsubscribeAssessments()
    if (unsubscribeAliases) unsubscribeAliases()
    const unsubA = userSessionAssessmentsOnSnapshot({ uid, sessionId, setAssessments })
    const unsubAliases = userTestAliasesOnSnapshot({ uid, setAliases })
    set({ unsubscribeAssessments: unsubA, unsubscribeAliases: unsubAliases })
  },

  unsubscribeAll: () => {
    const { unsubscribeAssessments, unsubscribeAliases } = get()
    if (unsubscribeAssessments) unsubscribeAssessments()
    if (unsubscribeAliases) unsubscribeAliases()
    set({ unsubscribeAssessments: null, unsubscribeAliases: null })
  },

  // Generic create — call from any source (AssessmentTestPanel, ROM, conditions tree, manual).
  // Caller supplies: testKey (stable join key), source, measurementType, pre payload.
  createPre: async ({
    sessionId,
    clientId,
    testKey,
    testLabel,
    conditionName,
    source,
    measurementType,
    pre,
    markedForRetest = false,
    aliasResolved = true,
  }) => {
    return firestoreCreatePreAssessment({
      sessionId,
      clientId,
      testKey,
      testLabel,
      conditionName,
      source,
      measurementType,
      pre,
      markedForRetest,
      aliasResolved,
    })
  },

  // Dictation-sourced (ad-hoc) — slug from label, look up alias, queue for resolution if unknown.
  createPreFromDictation: async ({
    sessionId,
    clientId,
    label,
    conditionName,
    source,
    measurementType,
    pre,
  }) => {
    const slug = buildAdHocTestKey(label)
    const existingAlias = get().aliases?.[slug] ?? (await firestoreGetTestAlias(slug))
    const testKey = existingAlias?.canonicalTestKey ?? slug
    const aliasResolved = !!existingAlias
    const docData = await firestoreCreatePreAssessment({
      sessionId,
      clientId,
      testKey,
      testLabel: existingAlias?.label ?? label,
      conditionName,
      source,
      measurementType,
      pre,
      aliasResolved,
    })
    if (!aliasResolved) {
      set(
        produce((state) => {
          state.pendingAliasQueue.push({
            slug,
            label,
            assessmentId: docData.assessmentId,
            suggestions: fuzzyMatchTestLabel(label),
          })
        })
      )
    }
    return docData
  },

  updatePre: async ({ sessionId, assessmentId, patch, editedBy = "user" }) =>
    firestoreUpdatePreAssessment({ sessionId, assessmentId, patch, editedBy }),

  writePost: async ({ sessionId, assessmentId, post, editedBy = "user" }) =>
    firestoreWritePostAssessment({
      sessionId,
      assessmentId,
      post: { status: POST_STATUS_RETESTED, ...post },
      editedBy,
    }),

  markNotRetested: async ({ sessionId, assessmentId, reason }) =>
    firestoreWritePostAssessment({
      sessionId,
      assessmentId,
      post: { status: POST_STATUS_NOT_RETESTED, notRetestedReason: reason ?? null },
      editedBy: "user",
    }),

  toggleMarkedForRetest: async ({ sessionId, assessmentId, markedForRetest }) =>
    firestoreSetMarkedForRetest({ sessionId, assessmentId, markedForRetest }),

  removeAssessment: async ({ sessionId, assessmentId }) =>
    firestoreDeleteAssessment({ sessionId, assessmentId }),

  resolveAlias: async ({ slug, label, canonicalTestKey, conditionNodesId, resolvedBy }) => {
    await firestoreResolveTestAlias({
      slug,
      label,
      canonicalTestKey,
      conditionNodesId,
      resolvedBy,
    })
    set(
      produce((state) => {
        state.pendingAliasQueue = state.pendingAliasQueue.filter((q) => q.slug !== slug)
      })
    )
  },

  setAssessmentMode: async ({ sessionId, mode }) =>
    firestoreSetSessionAssessmentMode({ sessionId, mode }),

  setSessionOutcome: async ({ sessionId, rating, note }) =>
    firestoreSetSessionOutcome({ sessionId, rating, note }),

  setClientOutcomeTrackingOptIn: async ({ clientId, optedIn }) =>
    firestoreSetClientOutcomeTrackingOptIn({ clientId, optedIn }),

  commitForSave: async ({ sessionId, clientId, sessionDate }) =>
    firestoreCommitSessionAssessments({ sessionId, clientId, sessionDate }),

  getRetestQueue: () => {
    const { assessments } = get()
    const needsPost = assessments.filter(
      (a) => !a.post || a.post.status !== POST_STATUS_RETESTED
    )
    return [
      ...needsPost.filter((a) => a.markedForRetest),
      ...needsPost.filter((a) => !a.markedForRetest),
    ]
  },

  // Helper for slug-based ad-hoc keys (re-exported so step-2 callers don't reach into utils)
  buildAdHocTestKey: (label) => normalizeToSlug(label),

  resetAssessmentStore: () => {
    const { unsubscribeAll } = get()
    unsubscribeAll()
    set({
      assessments: [],
      pendingAliasQueue: [],
      wrapUpOpen: false,
      dialogStep: null,
    })
  },
}))
